import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Pencil, Save, X, RotateCcw, Loader2 } from 'lucide-react';

interface EditableBioSectionProps {
  bio: string | null;
  isEdited: boolean;
  generatedBio: string | null;
  isLoading: boolean;
  isGenerating: boolean;
  isSaving: boolean;
  onGenerate: () => Promise<string | null>;
  onSave: (bio: string) => Promise<boolean>;
  onReset: () => Promise<boolean>;
}

export const EditableBioSection: React.FC<EditableBioSectionProps> = ({
  bio,
  isEdited,
  generatedBio,
  isLoading,
  isGenerating,
  isSaving,
  onGenerate,
  onSave,
  onReset,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedBio, setEditedBio] = useState(bio || '');

  useEffect(() => {
    setEditedBio(bio || '');
  }, [bio]);

  const handleSave = async () => {
    const success = await onSave(editedBio);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedBio(bio || '');
    setIsEditing(false);
  };

  const handleReset = async () => {
    const success = await onReset();
    if (success) {
      setIsEditing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Bio</h4>
        </div>
        <div className="h-20 bg-muted/50 rounded-lg animate-pulse" />
      </div>
    );
  }

  // No bio exists - show generate button
  if (!bio && !generatedBio) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Bio</h4>
        </div>
        <div className="p-4 rounded-lg bg-muted/30 border border-dashed border-muted-foreground/30 text-center">
          <p className="text-muted-foreground text-sm mb-3">No bio available</p>
          <Button 
            onClick={onGenerate} 
            disabled={isGenerating}
            size="sm"
            className="gap-2"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate with AI
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Bio</h4>
          {bio && (
            <Badge 
              variant="outline" 
              className={isEdited ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 'bg-primary/10 text-primary border-primary/20'}
            >
              {isEdited ? (
                <>
                  <Pencil className="h-3 w-3 mr-1" />
                  Edited
                </>
              ) : (
                <>
                  <Sparkles className="h-3 w-3 mr-1" />
                  AI Generated
                </>
              )}
            </Badge>
          )}
        </div>
        {!isEditing && bio && (
          <div className="flex items-center gap-1">
            {isEdited && generatedBio && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                disabled={isSaving}
                className="h-7 px-2 text-xs"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="h-7 px-2 text-xs"
            >
              <Pencil className="h-3 w-3 mr-1" />
              Edit
            </Button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-2">
          <Textarea
            value={editedBio}
            onChange={(e) => setEditedBio(e.target.value)}
            placeholder="Enter bio..."
            className="min-h-[100px] resize-none"
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isSaving || !editedBio.trim()}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm leading-relaxed">{bio}</p>
      )}

      {!isEditing && !bio && generatedBio && (
        <Button 
          onClick={onGenerate} 
          disabled={isGenerating}
          size="sm"
          variant="outline"
          className="gap-2"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Regenerating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Regenerate Bio
            </>
          )}
        </Button>
      )}
    </div>
  );
};
